<script setup>
import { computed, ref, watch } from 'vue'
import { Cropper, CircleStencil } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { useScrollLock } from '@/composables/useScrollLock'

/**
 * Cropping a profile picture.
 *
 * This was three hundred lines of hand-written canvas work: a drag handler, a
 * zoom slider, manual bounds arithmetic and a redraw on every mouse move. It
 * did the job on a desktop with a mouse and no better — no pinch, no touch
 * drag, no keyboard, no rotation, and the crop frame was a square with corner
 * marks drawn on top of a round avatar.
 *
 * vue-advanced-cropper is the same job done properly: a round stencil that
 * matches the avatar, wheel and pinch zoom, touch dragging, and bounds it
 * enforces itself. The contract is unchanged — it takes a File and emits the
 * cropped Blob — so every caller is untouched.
 */
const props = defineProps({
  show: { type: Boolean, default: false },
  imageFile: { type: File, default: null },
  isUploading: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'upload'])

useScrollLock(() => props.show)

const cropperRef = ref(null)
const imageDataUrl = ref(null)
const error = ref('')

/** The stored avatar is 512px square; cropping to that avoids upscaling later. */
const OUTPUT_SIZE = 512

watch(
  () => [props.show, props.imageFile],
  ([show, file]) => {
    if (!show || !file) {
      imageDataUrl.value = null

      return
    }

    error.value = ''
    const reader = new FileReader()
    reader.onload = (event) => { imageDataUrl.value = event.target.result }
    reader.onerror = () => { error.value = 'That image could not be read. Please choose another file.' }
    reader.readAsDataURL(file)
  },
  { immediate: true }
)

const canApply = computed(() => Boolean(imageDataUrl.value) && !props.isUploading)

const handleApply = () => {
  const result = cropperRef.value?.getResult()

  if (!result?.canvas) {
    error.value = 'Nothing to crop yet. Please wait for the image to load.'

    return
  }

  result.canvas.toBlob(
    (blob) => {
      if (!blob) {
        error.value = 'The crop could not be saved. Please try again.'

        return
      }

      emit('upload', blob, props.imageFile?.name ?? 'avatar.jpg')
    },
    'image/jpeg',
    0.92
  )
}

const handleCancel = () => {
  if (props.isUploading) return

  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/75 transition-opacity" @click="handleCancel"></div>

        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative w-full max-w-2xl transform rounded-2xl bg-surface shadow-xl transition-all" @click.stop>
            <div class="border-b border-line px-6 py-4">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-xl font-bold text-ink">Crop profile picture</h3>
                  <p class="mt-1 text-sm text-muted">Drag to reposition, pinch or scroll to zoom.</p>
                </div>
                <button
                  type="button"
                  class="text-subtle transition-colors hover:text-muted disabled:opacity-50"
                  :disabled="isUploading"
                  aria-label="Close"
                  @click="handleCancel"
                >
                  <span class="material-symbols-outlined text-2xl">close</span>
                </button>
              </div>
            </div>

            <div class="px-6 py-6">
              <div
                v-if="imageDataUrl"
                class="overflow-hidden rounded-xl bg-surface-2"
                style="height: min(60vh, 26rem)"
              >
                <Cropper
                  ref="cropperRef"
                  class="h-full"
                  :src="imageDataUrl"
                  :stencil-component="CircleStencil"
                  :stencil-props="{ aspectRatio: 1 }"
                  :canvas="{ width: OUTPUT_SIZE, height: OUTPUT_SIZE }"
                  image-restriction="fit-area"
                />
              </div>

              <p v-else class="py-12 text-center text-muted">Loading your image…</p>

              <p v-if="error" class="mt-3 text-sm text-red-500">{{ error }}</p>
            </div>

            <div class="flex justify-end gap-3 border-t border-line px-6 py-4">
              <button
                type="button"
                class="rounded-xl bg-surface-2 px-6 py-3 font-semibold text-ink transition-colors hover:opacity-90 disabled:opacity-50"
                :disabled="isUploading"
                @click="handleCancel"
              >
                Cancel
              </button>
              <button
                type="button"
                class="flex items-center rounded-xl bg-gradient-to-r from-teal-500 to-teal-800 px-8 py-3 font-bold text-white shadow-lg transition-all disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!canApply"
                @click="handleApply"
              >
                <span v-if="isUploading" class="material-symbols-outlined mr-2 animate-spin text-xl">progress_activity</span>
                {{ isUploading ? 'Uploading…' : 'Use this photo' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
